/**
 * One delivery client, and everything Ops manages for it.
 *
 * Three parts, top to bottom:
 *  - the client itself (name, active),
 *  - its delivery assignments (one feed over one transport to one destination),
 *  - its per-feed transforms (the reshape DSL config, edited as JSON).
 *
 * A client's *entitlement* is expressed through its assignments: each assignment
 * names the package it is served under, so the packages a client holds are the
 * ones its assignments reference. HARD RULE: `credentialRef` is an opaque
 * secret-store reference only (`secret://…`, `env:…`, `arn:aws:secretsmanager:…`)
 * — never a secret value. The admin API refuses anything else.
 */

import { useState } from "react";
import { useParams } from "react-router-dom";

import {
  Button,
  Card,
  CheckboxField,
  Crumbs,
  Empty,
  Field,
  FieldRow,
  Input,
  PageHead,
  Pill,
  Problem,
  Section,
  Select,
  Table,
  Td,
  Textarea,
  Th,
} from "@/components/kit";
import {
  createAssignmentAction,
  createTransformAction,
  deleteAssignmentAction,
  deleteTransformAction,
  updateAssignmentAction,
  updateClientAction,
  updateTransformAction,
} from "@/lib/actions/delivery";
import {
  getDeliveryClient,
  listClientFeedTransforms,
  listDeliveryAssignments,
  listFeedPackages,
} from "@/lib/api/client";
import type {
  ClientFeedTransform,
  DeliveryAssignment,
  DeliveryClient,
  FeedPackage,
} from "@/lib/api/types";
import { ApiError } from "@/lib/api/client";
import { useApi } from "@/spa/useApi";

const TRANSPORTS = ["webhook", "http_pull", "s3", "ftp", "sftp"];
const FORMATS = ["json", "xml"];

function errText(
  result: { error: string; violations: string[] } | undefined,
): string | undefined {
  if (!result) return undefined;
  return result.violations.length ? result.violations.join("; ") : result.error;
}

// --------------------------------------------------------------------- client
function ClientDetails({ client }: { client: DeliveryClient }) {
  const [name, setName] = useState(client.name);
  const [active, setActive] = useState(client.isActive);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(undefined);
    const result = await updateClientAction(client.id, {
      name: name.trim(),
      isActive: active,
    });
    setBusy(false);
    setError(errText(result));
  }

  return (
    <Section
      title="Client"
      note={`Code: ${client.code} — the opaque identity feeds are read by.`}
    >
      <FieldRow>
        <Field label="Name" htmlFor="cl-name">
          <Input
            id="cl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <CheckboxField
          label="Active"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
      </FieldRow>
      {error && (
        <p
          style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }}
          role="alert"
        >
          {error}
        </p>
      )}
      <div style={{ marginTop: "var(--s3)" }}>
        <Button tone="primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save client"}
        </Button>
      </div>
    </Section>
  );
}

// ----------------------------------------------------------------- assignment
type AssignFields = {
  packageId: string;
  feedKey: string;
  deliveryType: string;
  destination: string;
  credentialRef: string;
  format: string;
  throttleSeconds: string;
  scheduleSeconds: string;
  isActive: boolean;
};

function AssignmentForm({
  packages,
  initial,
  submitLabel,
  onSubmit,
}: {
  packages: FeedPackage[];
  initial: AssignFields;
  submitLabel: string;
  onSubmit: (f: AssignFields) => Promise<string | undefined>;
}) {
  const [f, setF] = useState<AssignFields>(initial);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof AssignFields>(k: K, v: AssignFields[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    const err = await onSubmit(f);
    setBusy(false);
    setError(err);
  }

  return (
    <form onSubmit={submit}>
      <FieldRow>
        <Field
          label="Feed key"
          required
          hint="calendar, medals_tally"
          htmlFor="a-feed"
        >
          <Input
            id="a-feed"
            value={f.feedKey}
            required
            placeholder="medals_tally"
            onChange={(e) => set("feedKey", e.target.value)}
          />
        </Field>
        <Field
          label="Package (entitlement)"
          hint="How this feed is entitled"
          htmlFor="a-pkg"
        >
          <Select
            id="a-pkg"
            value={f.packageId}
            onChange={(e) => set("packageId", e.target.value)}
          >
            <option value="">No package (internal)</option>
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </Select>
        </Field>
      </FieldRow>
      <FieldRow>
        <Field label="Transport" htmlFor="a-type">
          <Select
            id="a-type"
            value={f.deliveryType}
            onChange={(e) => set("deliveryType", e.target.value)}
          >
            {TRANSPORTS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Destination"
          required
          hint="A URL, bucket/prefix, or FTP path — never a secret"
          htmlFor="a-dest"
        >
          <Input
            id="a-dest"
            value={f.destination}
            required
            placeholder="ndtv-bucket/asian-games"
            onChange={(e) => set("destination", e.target.value)}
          />
        </Field>
      </FieldRow>
      <Field
        label="Credential reference"
        hint="An opaque reference only: secret://… , env:… or arn:aws:secretsmanager:… — never the secret itself"
        htmlFor="a-cred"
      >
        <Input
          id="a-cred"
          value={f.credentialRef}
          placeholder="secret://omnium/delivery/ndtv/1"
          onChange={(e) => set("credentialRef", e.target.value)}
        />
      </Field>
      <FieldRow>
        <Field label="Format" htmlFor="a-fmt">
          <Select
            id="a-fmt"
            value={f.format}
            onChange={(e) => set("format", e.target.value)}
          >
            {FORMATS.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="No more often than (seconds)"
          hint="0 = send every change"
          htmlFor="a-throttle"
        >
          <Input
            id="a-throttle"
            type="number"
            min={0}
            value={f.throttleSeconds}
            onChange={(e) => set("throttleSeconds", e.target.value)}
          />
        </Field>
        <Field
          label="At least every (seconds)"
          hint="0 = only when the file changes"
          htmlFor="a-schedule"
        >
          <Input
            id="a-schedule"
            type="number"
            min={0}
            value={f.scheduleSeconds}
            onChange={(e) => set("scheduleSeconds", e.target.value)}
          />
        </Field>
        <CheckboxField
          label="Active"
          checked={f.isActive}
          onChange={(e) => set("isActive", e.target.checked)}
        />
      </FieldRow>
      {error && (
        <p
          style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }}
          role="alert"
        >
          {error}
        </p>
      )}
      <div style={{ marginTop: "var(--s3)" }}>
        <Button
          tone="primary"
          type="submit"
          disabled={busy || !f.feedKey.trim() || !f.destination.trim()}
        >
          {busy ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function toThrottle(raw: string): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function AssignmentsSection({
  clientId,
  assignments,
  packages,
}: {
  clientId: string;
  assignments: DeliveryAssignment[];
  packages: FeedPackage[];
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const packageName = (id?: string | null) =>
    id ? (packages.find((p) => p.id === id)?.code ?? "—") : "—";

  return (
    <Section
      title="Delivery assignments"
      note="One feed over one transport to one destination. The package is the entitlement."
      actions={
        <Button tone="primary" onClick={() => setAdding((v) => !v)}>
          {adding ? "Close" : "Add assignment"}
        </Button>
      }
    >
      {adding && (
        <Card>
          <AssignmentForm
            packages={packages}
            submitLabel="Create assignment"
            initial={{
              packageId: "",
              feedKey: "",
              deliveryType: "webhook",
              destination: "",
              credentialRef: "",
              format: "json",
              throttleSeconds: "0",
              scheduleSeconds: "0",
              isActive: true,
            }}
            onSubmit={async (f) => {
              const result = await createAssignmentAction({
                clientId,
                packageId: f.packageId || null,
                feedKey: f.feedKey.trim(),
                deliveryType: f.deliveryType,
                destination: f.destination.trim(),
                credentialRef: f.credentialRef.trim() || null,
                format: f.format,
                throttleSeconds: toThrottle(f.throttleSeconds),
                scheduleSeconds: toThrottle(f.scheduleSeconds),
                isActive: f.isActive,
              });
              if (!result) setAdding(false);
              return errText(result);
            }}
          />
        </Card>
      )}

      {assignments.length === 0 ? (
        <Card>
          <Empty
            title="No assignments"
            note="Add one above to start delivering a feed to this client."
          />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Feed</Th>
              <Th>Transport</Th>
              <Th>Destination</Th>
              <Th>Package</Th>
              <Th>Cred ref</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {assignments.map((a) =>
              editing === a.id ? (
                <tr key={a.id}>
                  <Td colSpan={7}>
                    <AssignmentForm
                      packages={packages}
                      submitLabel="Save assignment"
                      initial={{
                        packageId: a.packageId ?? "",
                        feedKey: a.feedKey,
                        deliveryType: a.deliveryType,
                        destination: a.destination,
                        credentialRef: a.credentialRef ?? "",
                        format: a.format,
                        throttleSeconds: String(a.throttleSeconds),
                        scheduleSeconds: String(a.scheduleSeconds ?? 0),
                        isActive: a.isActive,
                      }}
                      onSubmit={async (f) => {
                        const result = await updateAssignmentAction(a.id, {
                          packageId: f.packageId || null,
                          feedKey: f.feedKey.trim(),
                          deliveryType: f.deliveryType,
                          destination: f.destination.trim(),
                          credentialRef: f.credentialRef.trim() || null,
                          format: f.format,
                          throttleSeconds: toThrottle(f.throttleSeconds),
                          scheduleSeconds: toThrottle(f.scheduleSeconds),
                          isActive: f.isActive,
                        });
                        if (!result) setEditing(null);
                        return errText(result);
                      }}
                    />
                  </Td>
                </tr>
              ) : (
                <tr key={a.id}>
                  <Td strong>{a.feedKey}</Td>
                  <Td>{a.deliveryType}</Td>
                  <Td quiet>{a.destination}</Td>
                  <Td quiet>{packageName(a.packageId)}</Td>
                  <Td quiet>{a.credentialRef ?? "—"}</Td>
                  <Td>
                    <Pill tone={a.isActive ? "good" : "neutral"} dot>
                      {a.isActive ? "Active" : "Inactive"}
                    </Pill>
                  </Td>
                  <Td>
                    <Button onClick={() => setEditing(a.id)}>Edit</Button>{" "}
                    <Button
                      tone="danger"
                      onClick={() => {
                        if (
                          window.confirm(`Remove the ${a.feedKey} assignment?`)
                        ) {
                          void deleteAssignmentAction(a.id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </Td>
                </tr>
              ),
            )}
          </tbody>
        </Table>
      )}
    </Section>
  );
}

// ------------------------------------------------------------------ transform
function prettyConfig(config: Record<string, unknown>): string {
  return JSON.stringify(config, null, 2);
}

function parseConfig(raw: string): {
  config?: Record<string, unknown>;
  error?: string;
} {
  const text = raw.trim();
  if (!text) return { config: {} };
  try {
    const parsed = JSON.parse(text) as unknown;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {
        error: 'The config must be a JSON object, e.g. { "rules": [...] }.',
      };
    }
    return { config: parsed as Record<string, unknown> };
  } catch {
    return { error: "That is not valid JSON." };
  }
}

const CONFIG_HINT =
  'The reshape DSL, e.g. {"rules":[{"op":"rename","from":"gold","to":"g"}]}. Empty = pass-through.';

function TransformForm({
  initialFeedType,
  initialConfig,
  feedTypeLocked,
  submitLabel,
  onSubmit,
}: {
  initialFeedType: string;
  initialConfig: string;
  feedTypeLocked?: boolean;
  submitLabel: string;
  onSubmit: (
    feedType: string,
    config: Record<string, unknown>,
  ) => Promise<string | undefined>;
}) {
  const [feedType, setFeedType] = useState(initialFeedType);
  const [configText, setConfigText] = useState(initialConfig);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const parsed = parseConfig(configText);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError(undefined);
    const err = await onSubmit(feedType.trim(), parsed.config ?? {});
    setBusy(false);
    setError(err);
  }

  return (
    <form onSubmit={submit}>
      <Field
        label="Feed type"
        required
        hint="calendar, medals_tally"
        htmlFor="t-feed"
      >
        <Input
          id="t-feed"
          value={feedType}
          required
          disabled={feedTypeLocked}
          placeholder="medals_tally"
          onChange={(e) => setFeedType(e.target.value)}
        />
      </Field>
      <Field
        label="Transform config (JSON)"
        hint={CONFIG_HINT}
        htmlFor="t-config"
      >
        <Textarea
          id="t-config"
          value={configText}
          rows={8}
          spellCheck={false}
          style={{ fontFamily: "var(--mono, monospace)" }}
          onChange={(e) => setConfigText(e.target.value)}
        />
      </Field>
      {error && (
        <p
          style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }}
          role="alert"
        >
          {error}
        </p>
      )}
      <div style={{ marginTop: "var(--s3)" }}>
        <Button
          tone="primary"
          type="submit"
          disabled={busy || !feedType.trim()}
        >
          {busy ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function TransformsSection({
  clientId,
  transforms,
}: {
  clientId: string;
  transforms: ClientFeedTransform[];
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <Section
      title="Per-feed transforms"
      note="How this client's feeds are reshaped from the canonical shape. No row = the canonical bytes."
      actions={
        <Button tone="primary" onClick={() => setAdding((v) => !v)}>
          {adding ? "Close" : "Add transform"}
        </Button>
      }
    >
      {adding && (
        <Card>
          <TransformForm
            initialFeedType=""
            initialConfig=""
            submitLabel="Create transform"
            onSubmit={async (feedType, config) => {
              const result = await createTransformAction({
                clientId,
                feedType,
                config,
              });
              if (!result) setAdding(false);
              return errText(result);
            }}
          />
        </Card>
      )}

      {transforms.length === 0 ? (
        <Card>
          <Empty
            title="No transforms"
            note="This client receives every feed in its canonical shape. Add one to reshape a feed."
          />
        </Card>
      ) : (
        transforms.map((t) => (
          <Card key={t.id}>
            {editing === t.id ? (
              <TransformForm
                initialFeedType={t.feedType}
                initialConfig={prettyConfig(t.config)}
                feedTypeLocked
                submitLabel="Save transform"
                onSubmit={async (_feedType, config) => {
                  const result = await updateTransformAction(t.id, {
                    config,
                    isActive: t.isActive,
                  });
                  if (!result) setEditing(null);
                  return errText(result);
                }}
              />
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "var(--s2)",
                  }}
                >
                  <span>
                    <strong>{t.feedType}</strong>{" "}
                    <Pill tone={t.isActive ? "good" : "neutral"} dot>
                      {t.isActive ? "Active" : "Inactive"}
                    </Pill>
                  </span>
                  <span>
                    <Button onClick={() => setEditing(t.id)}>Edit</Button>{" "}
                    <Button
                      tone="danger"
                      onClick={() => {
                        if (
                          window.confirm(`Remove the ${t.feedType} transform?`)
                        ) {
                          void deleteTransformAction(t.id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </span>
                </div>
                <pre
                  style={{
                    margin: 0,
                    fontSize: "var(--t-sm)",
                    overflowX: "auto",
                    color: "var(--muted)",
                  }}
                >
                  {prettyConfig(t.config)}
                </pre>
              </>
            )}
          </Card>
        ))
      )}
    </Section>
  );
}

// ------------------------------------------------------------------- the page
export function DeliveryClientPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { data, loading, error, cause } = useApi(
    async () => {
      const client = await getDeliveryClient(id);
      const [assignments, transforms, packages] = await Promise.all([
        listDeliveryAssignments(id),
        listClientFeedTransforms(id),
        listFeedPackages(),
      ]);
      return { client, assignments, transforms, packages };
    },
    [id],
    "failed to load",
  );

  if (error) {
    if (cause instanceof ApiError && cause.status === 404) {
      return <Problem>Delivery client not found.</Problem>;
    }
    return <Problem>Could not load the client: {error}</Problem>;
  }
  if (!data)
    return (
      <PageHead title="Delivery client" note={loading ? "Loading…" : ""} />
    );

  // Each entitled feed, with the package(s) that grant it — so an admin can see
  // *why* a feed is entitled, not just that it is.
  const entitledByFeed = new Map<string, string[]>();
  for (const a of data.assignments) {
    if (!a.isActive || !a.packageId) continue;
    const pkg = data.packages.find((p) => p.id === a.packageId);
    if (!pkg) continue;
    for (const k of pkg.feedKeys ?? []) {
      const names = entitledByFeed.get(k) ?? [];
      if (!names.includes(pkg.name)) names.push(pkg.name);
      entitledByFeed.set(k, names);
    }
  }
  const entitled = [...entitledByFeed.entries()].sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  return (
    <>
      <Crumbs
        trail={[
          { label: "Delivery", href: "/delivery" },
          { label: data.client.name },
        ]}
      />
      <PageHead
        title={data.client.name}
        note="Manage this client's assignments and transforms."
      />

      <ClientDetails client={data.client} />

      <Section
        title="Entitled feeds"
        note="Derived from the packages this client's active assignments are served under."
      >
        {entitled.length === 0 ? (
          <p style={{ color: "var(--muted)" }}>
            None yet — grant a feed by giving an active assignment a package
            below.
          </p>
        ) : (
          <span
            style={{
              display: "inline-flex",
              flexWrap: "wrap",
              gap: "var(--s2)",
            }}
          >
            {entitled.map(([k, pkgs]) => (
              <Pill key={k} tone="accent">
                {k} <span style={{ opacity: 0.65 }}>· {pkgs.join(", ")}</span>
              </Pill>
            ))}
          </span>
        )}
      </Section>

      <AssignmentsSection
        clientId={data.client.id}
        assignments={data.assignments}
        packages={data.packages}
      />

      <TransformsSection
        clientId={data.client.id}
        transforms={data.transforms}
      />
    </>
  );
}
